import { getName } from '@home/utils/common';
import React, { useMemo } from 'react';
import SvgIcon from '@home/components/SvgIcon';
import BasicImage from '@home/components/BasicImage';
import classNames from 'classnames';
import DatabarinSvg from '@/pages/home/assets/svg/dataSource/databarin.svg';
import dataFormat from '@home/utils/dbDataFormat/dataFormat';
import { useIntl } from 'umi';
import { LANGUAGE_EN } from '@home/_config/config';
import Loading from '@home/components/Loading';
import moment from 'moment';
import { buriedBtnPoint } from '@home/utils/buriedPoint';
import './index.scss';

export default function VsBenchmark(props: any) {
  const {
    titleEn = 'Benchmark',
    titleCn = '行业基准',
    moreEn = 'More',
    moreCn = '更多',
    socNameEn = 'SOC Games',
    socNameCn = 'SOC 游戏',
    gameDetailNameEn = 'Game Details',
    gameDetailNameCn = '游戏详情',
    day1NameEn = 'Day 1',
    day1NameCn = '首日',
    week1NameEn = 'Week 1',
    week1NameCn = '首周',
    month1NameEn = 'Month 1',
    month1NameCn = '首月',
    bottomMessageEn,
    bottomMessageCn,
    gameListData,
    topMedianData: topMedianDataProps,
    topMianKeyConfig = {
      top10: {
        titleEn: "Top 10%",
        titleCn: "前10%",
        color: "#5086F3",
      },
      median: {
        titleEn: "Median",
        titleCn: "中位数",
        color: "#69D19C",
      },
    },
    metricColumn,
    valueFormat = 'bmk00',
    currentGameReleaseTime,
    defaultEndTime,
    vsBenchmarkLoading,
    jumpUrl,
    sortCallBack,
    btnLogConfig,
    gameBtnLogConfig,
    logConfig,
    buttonId,
    gameButtonId,
    filter = {},
    // vsBenchmarkError,
  } = props;

  const { locale } = useIntl();
  const isEn = locale === LANGUAGE_EN;

  const { metricKey: oldMetrickKey, vsBenchmarkMapKey } = metricColumn || {};
  const metricKey = vsBenchmarkMapKey || oldMetrickKey;

  const {
    gameDetail: gameDetailFilter = 'hideIfAnyEmpty',
  }: { gameDetail: 'hideIfAllEmpty' | 'hideIfAnyEmpty'; } = filter || {};

  const topMedianData = useMemo(() => {
    const availableKeys = Object.keys(topMianKeyConfig);

    return topMedianDataProps?.[metricKey]?.filter((item: any) => availableKeys?.includes(item.key));
  }, [topMedianDataProps, metricKey, topMianKeyConfig]);

  const title = getName(isEn, titleEn, titleCn);
  const socName = getName(isEn, socNameEn, socNameCn);
  const gameDetailName = getName(isEn, gameDetailNameEn, gameDetailNameCn);
  const day1Name = getName(isEn, day1NameEn, day1NameCn);
  const week1Name = getName(isEn, week1NameEn, week1NameCn);
  const month1Name = getName(isEn, month1NameEn, month1NameCn);
  const moreText = getName(isEn, moreEn, moreCn);
  const bottomMessage = getName(isEn, bottomMessageEn, bottomMessageCn);

  const { topMedianList, gameList, sortValue } = useMemo(() => {
    // 过滤竞品中的空数据
    const gameList =
      gameListData?.[metricKey]?.filter(
        (item: any) => {
          if (gameDetailFilter === 'hideIfAllEmpty') {
            return (item?.day1 || item?.week1 || item?.month1) || item?.isCurrentGame;
          }
          return (item?.day1 && item?.week1 && item?.month1) || item?.isCurrentGame;
        }
      ) || [];

    // 上线时间7天内，根据day1排序，超过一周根据week1，超过一个月根据month1
    let sortValue = 'day1';
    const diffDay = moment(defaultEndTime).diff(
      moment(currentGameReleaseTime),
      'days',
    );
    if (diffDay > 30) {
      sortValue = 'month1';
    } else if (diffDay > 7) {
      sortValue = 'week1';
    }

    if (typeof sortCallBack === 'function') {
      sortValue = sortCallBack(diffDay);
    }
    gameList.sort((a: any, b: any) => {
      return Number(b[sortValue] || '') - Number(a[sortValue] || '');
    });

    return {
      topMedianList: topMedianData || [],
      gameList,
      sortValue,
    };
  }, [
    gameListData,
    topMedianData,
    metricKey,
    currentGameReleaseTime,
    defaultEndTime,
  ]);

  const jumpToLink = (url: string, title: string, logBtnId: string) => {
    if (url) {
      window.open(url);
    }
    const logObj = {
      buttonId: logBtnId,
      extInfo: title,
      pageId: logConfig?.pageId,
      typeId: 'dashboard_pc_v2',
      ...btnLogConfig,
      ...gameBtnLogConfig,
    };
    buriedBtnPoint(logObj);
  };

  return (
    <div
      className={classNames('dbd-vs-benchmark-container', {
        'dbd-vs-benchmark-loading': vsBenchmarkLoading,
      })}
      onClick={(e) => e.stopPropagation()}
    >
      {vsBenchmarkLoading ? (
        <Loading />
      ) : (
        <>
          <div className="dbd-vs-benchmark-sticky-header">
            <div className="dbd-vs-benchmark-title">
              <div className="dbd-vs-benchmark-title-text">
                <BasicImage src={DatabarinSvg} />
                {title}
              </div>
              <div className="dbd-vs-benchmark-title-more"  onClick={() => jumpToLink(jumpUrl, title, buttonId)}>
                {moreText} <SvgIcon type="link-to" />
              </div>
            </div>
            <div className="dbd-vs-benchmark-th-row">
              <div className="dbd-vs-benchmark-first-column dbd-vs-benchmark-column">
                {socName}
              </div>
              <div className="dbd-vs-benchmark-column">{day1Name}</div>
              <div className="dbd-vs-benchmark-column">{week1Name}</div>
              <div className="dbd-vs-benchmark-column">{month1Name}</div>
            </div>
          </div>
          {topMedianList.map((item: any) => {
            const { key, day1, week1, month1 } = item;
            return (
              <div className="dbd-vs-benchmark-td-row" key={key}>
                <div className="dbd-vs-benchmark-first-column dbd-vs-benchmark-column dbd-vs-benchmark-column-metric">
                  <div
                    className="dbd-vs-benchmark-column-metric-icon"
                    style={{
                      background: topMianKeyConfig?.[key]?.color,
                    }}
                  ></div>
                  { isEn ? topMianKeyConfig?.[key]?.titleEn : topMianKeyConfig?.[key]?.titleCn }
                </div>
                <div className="dbd-vs-benchmark-column">
                  {dataFormat({ value: day1, formatRule: valueFormat })}
                </div>
                <div className="dbd-vs-benchmark-column">
                  {dataFormat({ value: week1, formatRule: valueFormat })}
                </div>
                <div className="dbd-vs-benchmark-column">
                  {dataFormat({ value: month1, formatRule: valueFormat })}
                </div>
              </div>
            );
          })}
          <div className="dbd-vs-benchmark-row-line"></div>
          <div className="dbd-vs-benchmark-th-row">
            <div className="dbd-vs-benchmark-first-column dbd-vs-benchmark-column">
              {gameDetailName}
            </div>
            <div
              className={classNames('dbd-vs-benchmark-column', {
                'dbd-vs-benchmark-th-row-current': sortValue === 'day1',
              })}
            >
              {day1Name}
              {sortValue === 'day1' && <SvgIcon type="down-arrow" />}
            </div>
            <div
              className={classNames('dbd-vs-benchmark-column', {
                'dbd-vs-benchmark-th-row-current': sortValue === 'week1',
              })}
            >
              {week1Name}
              {sortValue === 'week1' && <SvgIcon type="down-arrow" />}
            </div>
            <div
              className={classNames('dbd-vs-benchmark-column', {
                'dbd-vs-benchmark-th-row-current': sortValue === 'month1',
              })}
            >
              {month1Name}
              {sortValue === 'month1' && <SvgIcon type="down-arrow" />}
            </div>
          </div>
          {gameList.map((item: any, index: number) => {
            const { gameName, day1, week1, month1, isCurrentGame, cover, gamesDetailsUrl} =
              item;
            return (
              <div
                className={classNames('dbd-vs-benchmark-td-row', {
                  'dbd-vs-benchmark-td-row-current': isCurrentGame,
                })}
                key={gameName}
              >
                <div className="dbd-vs-benchmark-first-column dbd-vs-benchmark-column dbd-vs-benchmark-column-game"
                  onClick={() => jumpToLink(gamesDetailsUrl, gameName, gameButtonId)}>
                  <div>{index + 1}</div>
                  <BasicImage src={cover} />
                  <div
                    className="dbd-vs-benchmark-column-game-name"
                    title={gameName}
                  >
                    {gameName}
                  </div>
                </div>
                <div
                  className={classNames('dbd-vs-benchmark-column', {
                    'dbd-vs-benchmark-td-row-current-value':
                      sortValue === 'day1',
                  })}
                >
                  {dataFormat({ value: day1, formatRule: valueFormat })}
                </div>
                <div
                  className={classNames('dbd-vs-benchmark-column', {
                    'dbd-vs-benchmark-td-row-current-value':
                      sortValue === 'week1',
                  })}
                >
                  {dataFormat({ value: week1, formatRule: valueFormat })}
                </div>
                <div
                  className={classNames('dbd-vs-benchmark-column', {
                    'dbd-vs-benchmark-td-row-current-value':
                      sortValue === 'month1',
                  })}
                >
                  {dataFormat({ value: month1, formatRule: valueFormat })}
                </div>
              </div>
            );
          })}
          {bottomMessage && <div className="dbd-vs-benchmark-footer-text">{bottomMessage}</div>}
        </>
      )}
    </div>
  );
}
